import React from 'react';
import GlobalProps from '../../../components/globalProps';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import {
  RequestMembership,
  createMembership,
  updateMembership,
  deleteMembership,
  MembershipPermission,
} from '../../../model/membership';
import { handleError } from '../../../model/notification';
import { Button } from '../../../components/Button/Button';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { t } from '../../../locales/translate';
import { compact } from 'lodash';
import { Stack } from '../../../components/Stack/Stack';
import { FormForm } from '../../../components/Form/FormForm';
import { FormSelect } from '../../../components/Form/Select/FormSelect';
import { FormTextField } from '../../../components/Form/TextField/FormTextField';
import { FormCheckbox } from '../../../components/Form/Checkbox/FormCheckbox';
import { FormSearch } from '../../../components/Form/AsyncSearch/FormSearch';
import { FormTextArea } from '../../../components/Form/TextArea/FormTextArea';
import { sendInvite } from '../../../model/invite';
import { FormDisplay } from '../../../components/Form/FormDisplay/FormDisplay';
import { formatDate } from '../../../util/formatters';
import { Company } from '../../../model/company';
import { MembershipRole } from '../../../generated/graphql';

interface Props extends GlobalProps {
  action?: 'invite' | 'add';
  membership?: Partial<RequestMembership>;
  company: Company;
  isKs?: boolean;
  isExpired?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
}

export function MembershipsForm({
  action,
  membership,
  company,
  isKs,
  isExpired,
  onSuccess,
  onClose,
}: Props): JSX.Element {
  const membershipId = membership?.id;
  const { model, fields, valid, dirty, onSubmit, submissionStatus } = useForm<
    RequestMembership & {
      isAdmin?: boolean;
      isMerchAdmin?: boolean;
      isTicketingAdmin?: boolean;
      isFinancialAdmin?: boolean;
    }
  >({
    model: {
      ...membership,
      email: membership?.invitedEmail,
      companyId: membership?.companyId || company.id,
      role: membership?.role || 'observer',
      isAdmin: membership?.permissions?.includes('admin'),
      isMerchAdmin: membership?.permissions?.includes('merch_admin'),
      isTicketingAdmin: membership?.permissions?.includes('ticketing_admin'),
      isFinancialAdmin: membership?.permissions?.includes('financial'),
    },
    onSubmit: async () => {
      try {
        const permissions = compact([
          ...(model.isAdmin ? (['admin'] as MembershipPermission[]) : []),
          ...(model.isMerchAdmin ? (['merch_admin'] as MembershipPermission[]) : []),
          ...(model.isTicketingAdmin ? (['ticketing_admin'] as MembershipPermission[]) : []),
          ...(model.isFinancialAdmin ? (['financial'] as MembershipPermission[]) : []),
        ]);

        const { userId, companyId, role, comment, email } = model;

        // Invite user
        if (action === 'invite') {
          if (!membershipId) {
            if (email && role && companyId) {
              await sendInvite({ email, role, companyId, comment: comment ?? null });
            }
          } else {
            if (role && companyId) {
              await updateMembership(membershipId, { role, comment, companyId, permissions });
            }
          }
          return onSuccess?.();
        }

        // Add a registered user
        if (action === 'add') {
          if (companyId && role) {
            const submitModel: RequestMembership = { role, userId, comment, companyId, permissions };

            if (!membershipId) {
              await createMembership(submitModel);
            } else {
              await updateMembership(membershipId, submitModel);
            }
          }
          return onSuccess?.();
        }
      } catch (e) {
        handleError(e);
      }
    },
    onSubmitError: (error) => handleError(error),
    validations: {
      email: action === 'invite' ? 'required' : undefined,
      userId: action === 'add' ? 'required' : undefined,
      role: 'required',
    },
  });

  const { confirm } = useConfirmation({
    title: t.actions.delete(t.model.membership._name),
    message: t.general.confirmDelete(t.model.membership._name),
    action: async () => {
      if (membershipId) {
        await deleteMembership(membershipId);
        onSuccess?.();
      }
    },
  });

  const canGrantFinancialPermission = membership?.$permissions?.includes('grant_financial');

  return (
    <div className="membership-modal">
      <FormForm
        onSubmit={onSubmit}
        onClose={onClose}
        canSubmit={dirty && valid}
        loading={submissionStatus === 'submitting'}
        saveButtonTitle={action === 'invite' && !membershipId ? t.model.membership.sendInvite : undefined}
        hideSubmit={isExpired}
      >
        <Stack>
          {action === 'invite' ? (
            <>
              {membershipId ? (
                <FormDisplay label={t.model.user.email} value={model.email} />
              ) : (
                <FormTextField type="email" {...fields.email} label={t.model.user.email} />
              )}
            </>
          ) : (
            <FormSearch
              {...fields.userId}
              name="user"
              searchIn={['user']}
              label={t.model.user._name}
              disabled={!!membershipId}
            />
          )}
          {action === 'invite' && membershipId && isExpired ? (
            <>
              <FormDisplay label={t.model.membership.role} value={model.role} />
              <FormDisplay label={t.model.tour.comment} value={model.comment} />
            </>
          ) : (
            <>
              <FormSelect
                {...fields.role}
                name="role"
                label={t.model.membership.role}
                onChange={(value) => fields.role.onChange(value as MembershipRole)}
                options={company.assignableRoles?.map((r) => ({
                  value: r,
                  label: t.model.membership[r as 'admin' | 'owner'],
                }))}
              />
              <FormTextArea {...fields.comment} label={t.model.tour.comment} />
            </>
          )}
          {isKs && (
            <>
              {action === 'invite' && membershipId && isExpired ? (
                <Stack line>
                  <FormDisplay label={t.model.membership.admin} value={model.isAdmin ? 'Yes' : 'No'} />
                  <FormDisplay label={t.model.membership.merchAdmin} value={model.isMerchAdmin ? 'Yes' : 'No'} />
                  <FormDisplay
                    label={t.model.membership.ticketingAdmin}
                    value={model.isTicketingAdmin ? 'Yes' : 'No'}
                  />
                  <FormDisplay
                    label={t.model.membership.financialAdmin}
                    value={model.isFinancialAdmin ? 'Yes' : 'No'}
                  />
                </Stack>
              ) : (
                <>
                  <FormCheckbox {...fields.isAdmin} label={t.model.membership.admin} forceSpacing />
                  <FormCheckbox {...fields.isMerchAdmin} label={t.model.membership.merchAdmin} forceSpacing />
                  <FormCheckbox {...fields.isTicketingAdmin} label={t.model.membership.ticketingAdmin} forceSpacing />
                  <FormCheckbox
                    {...fields.isFinancialAdmin}
                    label={t.model.membership.financialAdmin}
                    forceSpacing
                    description={t.model.membership.financialAdminDescription}
                  />
                </>
              )}
            </>
          )}
          {!isKs && canGrantFinancialPermission ? (
            <FormCheckbox
              {...fields.isFinancialAdmin}
              label={t.model.membership.financialAdmin}
              forceSpacing
              description={t.model.membership.financialAdminDescription}
            />
          ) : !isKs && fields.isFinancialAdmin.value ? (
            <FormCheckbox
              {...fields.isFinancialAdmin}
              label={t.model.membership.financialAdmin}
              forceSpacing
              readOnly
              description={t.model.membership.financialAdminDescription}
            />
          ) : null}

          {membershipId && (
            <Stack>
              {action === 'invite' && (
                <>
                  <FormDisplay label={t.model.membership.dateOfInvite} value={formatDate(membership?.createdAt)} />
                  <FormDisplay label={t.model.membership.linkExpiration} value={formatDate(membership?.expiresAt)} />
                </>
              )}
              {membership?.$permissions?.includes('destroy') && (
                <footer>
                  <Button title={t.actions.delete(t.model.membership._name)} type="secondary" onClick={confirm} />
                </footer>
              )}
            </Stack>
          )}
        </Stack>
      </FormForm>
    </div>
  );
}
